// Client for AICOS's app-owned auth (ADR-AUTH-001). Password login against
// AICOS's own /auth/login, secure token storage with expiry-aware refresh, a
// server-verified identity via /whoami, and a fetch wrapper that attaches the
// bearer token and refreshes proactively (and on 401).
export interface Tokens {
  access_token: string;
  refresh_token: string;
  expires_in?: number; // seconds
  expires_at?: number; // epoch ms (derived)
}

export interface Identity {
  sub?: string;
  email?: string;
  tenant_id?: string;
  roles?: string[];
  actor_type?: string;
}

const STORE_KEY = "aicos.tokens";
const SKEW_MS = 30_000; // refresh this long before expiry

function withExpiry(t: Tokens): Tokens {
  if (t.expires_in && !t.expires_at) t.expires_at = Date.now() + t.expires_in * 1000;
  return t;
}

export function loadTokens(): Tokens | null {
  const raw = localStorage.getItem(STORE_KEY);
  return raw ? (JSON.parse(raw) as Tokens) : null;
}

export function saveTokens(t: Tokens): void {
  localStorage.setItem(STORE_KEY, JSON.stringify(withExpiry(t)));
}

export function clearTokens(): void {
  localStorage.removeItem(STORE_KEY);
}

export async function login(email: string, password: string): Promise<Tokens> {
  const res = await fetch("/api/v1/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) throw new Error("invalid credentials");
  const t = (await res.json()) as Tokens;
  saveTokens(t);
  return t;
}

export async function refresh(): Promise<Tokens | null> {
  const t = loadTokens();
  if (!t) return null;
  const res = await fetch("/api/v1/auth/refresh", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: t.refresh_token }),
  });
  if (!res.ok) {
    clearTokens();
    return null;
  }
  const next = (await res.json()) as Tokens;
  saveTokens(next);
  return next;
}

// validToken returns a non-expired access token, refreshing proactively if it
// is within the skew window of expiry.
async function validToken(): Promise<string | null> {
  let t = loadTokens();
  if (!t) return null;
  if (t.expires_at && Date.now() > t.expires_at - SKEW_MS) {
    t = await refresh();
    if (!t) return null;
  }
  return t.access_token;
}

// apiFetch attaches the bearer token (refreshing proactively) and retries once
// on a 401 by refreshing.
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const withAuth = (token: string): RequestInit => ({
    ...init,
    headers: { ...(init.headers || {}), Authorization: `Bearer ${token}` },
  });
  const token = await validToken();
  if (!token) throw new Error("not authenticated");
  let res = await fetch(path, withAuth(token));
  if (res.status === 401) {
    const refreshed = await refresh();
    if (!refreshed) throw new Error("session expired");
    res = await fetch(path, withAuth(refreshed.access_token));
  }
  return res;
}

// whoami returns the server-verified identity (the token validated server-side).
export async function whoami(): Promise<Identity | null> {
  try {
    const res = await apiFetch("/api/v1/whoami");
    if (!res.ok) return null;
    return (await res.json()) as Identity;
  } catch {
    return null;
  }
}
